import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { MAX_STUDENT_NAME_LENGTH } from '@helpers/constants';
import OutsideClickHandler from '@helpers/hoc/close/outside-click-handler';
import useContextModal from '@helpers/hooks/use-context-modal';

import { selectAssignmentHashKeys, unselectAssignmentHashKeys } from '@containers/upf-assign/upf-assign-actions';
import { activateModal } from '@containers/modal-manager/modal-manager-actions';

import Checkbox from '@components/checkbox';
import { CHECKED, UNCHECKED, INDETERMINATE } from '@components/checkbox/constants';
import ContextModal from '@components/context-modal';
import { viewCurrentAssignmentsModal } from '@components/current-assignment-modal';
import { getAssignmentHashKey, buildAssignmentHashKeys } from '@components/status-cell/helpers';
import { useTracking } from '../../../analytics';

import { findStudentFullName, truncateStringEllipsis } from '../helpers';

import ContextModalContent from './context-modal-content';
import { AssignmentCountText, CheckboxWrapper, Root, StudentItem } from './styled';

const StudentListItem = props => {
  const { studentId, i18n } = props;

  /**
   * Store
   */
  const students = useSelector(state => state.entitiesReducer.students);
  const groups = useSelector(state => state.entitiesReducer.groups);
  const classrooms = useSelector(state => state.entitiesReducer.classrooms);
  const selectedLearningJourneyIds = useSelector(state => state.UPFAssignReducer.selectedLearningJourneyIds);
  const selectedAssignmentHashKeys = useSelector(state => state.UPFAssignReducer.selectedAssignmentHashKeys);

  /**
   * Hooks
   */
  const dispatch = useDispatch();
  const { trackAssignmentHistoryView, trackGridCheckboxClick } = useTracking();
  const { targetRef, contentModalProps, closeContextMenu, showContextModal } = useContextModal({
    leftAlign: true
  });

  /**
   * Local variables
   */
  const student = students[studentId] || {};
  const group = groups[student.groupId] || {};
  const classroom = classrooms[group?.classroomId] || {};

  const { incompleteAssignmentsCount = 0, studentUnavailableOverdueAssignmentsCount = 0 } = student;
  
  const { name: groupName } = group;
  const { name: classroomName } = classroom;
  const studentFullName = findStudentFullName(student);
  const studentAssignmentHashKeys = selectedLearningJourneyIds.map(learningJourneyId =>
    getAssignmentHashKey(studentId, learningJourneyId)
  );

  /**
   * Functions
   */
  const handleOnClick = checkboxState => {
    const isSelected = checkboxState === CHECKED;
    const assignmentHashKeys = buildAssignmentHashKeys({
      studentIds: [studentId],
      learningJourneyIds: selectedLearningJourneyIds
    });

    closeContextMenu();

    trackGridCheckboxClick({
      scope: 'student',
      studentId,
      selected: isSelected,
      count: selectedLearningJourneyIds.length
    });

    if (isSelected) return dispatch(selectAssignmentHashKeys(assignmentHashKeys));

    return dispatch(unselectAssignmentHashKeys(assignmentHashKeys));
  };

  const getState = () => {
    const selectedAssignmentCount = studentAssignmentHashKeys.filter(
      assignmentHashKey => !!selectedAssignmentHashKeys[assignmentHashKey]
    ).length;

    const studentAssignmentCount = studentAssignmentHashKeys.length;

    if (selectedAssignmentCount === 0) return UNCHECKED;
    if (selectedAssignmentCount === studentAssignmentCount) return CHECKED;
    if (selectedAssignmentCount < studentAssignmentCount) return INDETERMINATE;

    return UNCHECKED;
  };

  const assignmentCount = incompleteAssignmentsCount - studentUnavailableOverdueAssignmentsCount;

  return (
    <Root>
      <StudentItem data-testid="student-item" ref={targetRef} onClick={showContextModal}>
        <span>{truncateStringEllipsis(studentFullName, MAX_STUDENT_NAME_LENGTH)}</span>
        <AssignmentCountText>{assignmentCount}</AssignmentCountText>
      </StudentItem>
      <CheckboxWrapper>
        <Checkbox checkboxHandler={handleOnClick} state={getState()} />
      </CheckboxWrapper>
      <OutsideClickHandler callback={closeContextMenu} forButton={`studentItem_${studentId}`}>
        <ContextModal {...contentModalProps}>
          <ContextModalContent
            classroomName={classroomName}
            groupName={groupName}
            i18n={i18n}
            onClickCurrentAssignmentModalCallback={() => {
              trackAssignmentHistoryView({ studentId });
              dispatch(activateModal(viewCurrentAssignmentsModal(student, { label: classroomName })));
            }}
            onClickStudentCallback={() => handleOnClick(CHECKED)}
            studentFullName={studentFullName}
          />
        </ContextModal>
      </OutsideClickHandler>
    </Root>
  );
};

StudentListItem.propTypes = {
  i18n: PropTypes.object,
  studentId: PropTypes.number.isRequired
};

export default StudentListItem;
